/**
 * Time Clock — helpers and pure functions.
 *
 * Centralized so both `TimeTracking.js` (Today view) and
 * `TimesheetsView.jsx` (Phase 2) share consistent math + types.
 */

export const STORAGE_KEY = 'maidly_timeEntries';
export const PTO_KEY = 'maidly_ptoRequests';

// ----- Date utilities -----
export const pad2 = (n) => String(n).padStart(2, '0');
export const toISODate = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  return dt.toISOString().slice(0, 10);
};
export const formatHM = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  // 12-hour format (e.g. "9:00 AM", "5:30 PM")
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
};
export const formatDuration = (mins) => {
  if (!mins || mins <= 0) return '00:00';
  const h = Math.floor(mins / 60);
  const m = Math.floor(mins % 60);
  return `${pad2(h)}:${pad2(m)}`;
};
export const minutesBetween = (startIso, endIso) => {
  if (!startIso) return 0;
  const start = new Date(startIso).getTime();
  const end = endIso ? new Date(endIso).getTime() : Date.now();
  return Math.max(0, (end - start) / 60000);
};

// Start of week (Sunday) for a given date
export const startOfWeek = (d) => {
  const dt = new Date(d);
  dt.setHours(0, 0, 0, 0);
  dt.setDate(dt.getDate() - dt.getDay());
  return dt;
};

// Return Date[] of length 7 starting at the Sunday of the given date
export const weekDays = (d) => {
  const start = startOfWeek(d);
  const out = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(start);
    day.setDate(start.getDate() + i);
    out.push(day);
  }
  return out;
};

// Break minute math
export const totalBreakMinutes = (breaks = []) =>
  breaks.reduce((acc, b) => acc + (b.endTime ? minutesBetween(b.startTime, b.endTime) : 0), 0);

// Effective worked minutes (start->end minus completed breaks)
export const liveWorkedMinutes = (entry) => {
  if (!entry?.startTime) return 0;
  const gross = minutesBetween(entry.startTime, entry.endTime);
  return Math.max(0, gross - totalBreakMinutes(entry.breaks));
};

// ----- Audit log helper -----
export const auditEvent = (user, action, changes = null) => ({
  at: new Date().toISOString(),
  by: user?.email || 'system',
  byName: user?.name || 'System',
  action,
  ...(changes ? { changes } : {}),
});

// Compute a diff between two entry objects for audit log.
// Only tracks the fields humans edit; ignores derived (hours/pay).
export const diffEntry = (before, after) => {
  const fields = ['startTime', 'endTime', 'job', 'employeeNote', 'managerNote', 'type'];
  const changes = {};
  fields.forEach((f) => {
    if ((before?.[f] || '') !== (after?.[f] || '')) {
      changes[f] = [before?.[f] || null, after?.[f] || null];
    }
  });
  return Object.keys(changes).length ? changes : null;
};

// Normalize an entry — fill in Phase-2 fields with safe defaults.
export const normalizeEntry = (e) => ({
  type: 'shift',
  employeeNote: '',
  managerNote: '',
  attachments: [],
  approvalStatus: 'pending',
  approvedBy: null,
  approvedAt: null,
  auditLog: [],
  job: '',
  breaks: [],
  ...e,
});

// Weekly stats for a single employee across an array of entries
export const computeWeeklyStats = (entries, employeeId, weekStart) => {
  const start = new Date(weekStart);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 7);

  const inWeek = entries.filter(
    (e) =>
      e.employeeId === employeeId &&
      new Date(e.startTime) >= start &&
      new Date(e.startTime) < end
  );
  let regularMin = 0;
  let ptoMin = 0;
  let unpaidMin = 0;
  let workedDays = 0;
  const days = new Set();
  inWeek.forEach((e) => {
    const mins = liveWorkedMinutes(e);
    days.add(toISODate(new Date(e.startTime)));
    if (e.type === 'pto' || e.type === 'sick') ptoMin += mins;
    else if (e.type === 'unpaid') unpaidMin += mins;
    else regularMin += mins;
  });
  workedDays = days.size;
  return {
    regularMin,
    ptoMin,
    unpaidMin,
    totalPaidMin: regularMin + ptoMin,
    workedDays,
    entries: inWeek,
  };
};

// Generate or accept a stable id
export const newId = (prefix = 't') =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

// ============================================
// PHASE 3 HELPERS
// ============================================

// Detect overlapping shifts for the same employee. Returns a list of
// conflict pairs {a, b, employeeId, employeeName, overlapMin}. Used for
// double-booking warnings.
export const detectConflicts = (entries = []) => {
  const byEmp = {};
  entries.forEach((e) => {
    if (!e.startTime || !e.endTime) return; // only finished shifts
    if (e.type && e.type !== 'shift') return; // PTO/sick don't conflict
    (byEmp[e.employeeId] ||= []).push(e);
  });
  const conflicts = [];
  Object.values(byEmp).forEach((list) => {
    const sorted = [...list].sort((a, b) => a.startTime.localeCompare(b.startTime));
    for (let i = 0; i < sorted.length; i++) {
      for (let j = i + 1; j < sorted.length; j++) {
        const a = sorted[i];
        const b = sorted[j];
        if (b.startTime >= a.endTime) break; // sorted, no more overlap with `a`
        const startOverlap = b.startTime;
        const endOverlap = a.endTime < b.endTime ? a.endTime : b.endTime;
        const overlapMin = minutesBetween(startOverlap, endOverlap);
        if (overlapMin > 0) {
          conflicts.push({
            a, b,
            employeeId: a.employeeId,
            employeeName: a.employeeName,
            overlapMin,
          });
        }
      }
    }
  });
  return conflicts;
};

// Overtime calculation for one employee over one work week.
// Default rule: anything beyond 40 regular hours/week is paid at 1.5x.
export const computeOvertime = (entries, employeeId, weekStart, opts = {}) => {
  const otThreshold = opts.threshold ?? 40;
  const otMultiplier = opts.multiplier ?? 1.5;
  const stats = computeWeeklyStats(entries, employeeId, weekStart);
  const regHours = stats.regularMin / 60;
  const ptoHours = stats.ptoMin / 60;
  const otHours = Math.max(0, regHours - otThreshold);
  const straightHours = Math.min(regHours, otThreshold);
  return {
    regularHours: parseFloat(straightHours.toFixed(2)),
    overtimeHours: parseFloat(otHours.toFixed(2)),
    ptoHours: parseFloat(ptoHours.toFixed(2)),
    otMultiplier,
  };
};

// Sum payroll for a date range across all employees. Returns array of
// per-employee rows + a grand total.
export const computePayroll = (entries, employees, startDate, endDate) => {
  const start = new Date(startDate); start.setHours(0, 0, 0, 0);
  const end = new Date(endDate); end.setHours(23, 59, 59, 999);
  const inRange = entries.filter(
    (e) => new Date(e.startTime) >= start && new Date(e.startTime) <= end
  );

  // Group entries by week-of-startTime so we can apply OT per week
  const weekKey = (iso) => toISODate(startOfWeek(iso));
  const byEmpWeek = {};
  inRange.forEach((e) => {
    const key = `${e.employeeId}|${weekKey(e.startTime)}`;
    (byEmpWeek[key] ||= []).push(e);
  });

  const rowByEmp = {};
  Object.entries(byEmpWeek).forEach(([key, ents]) => {
    const [empId, wk] = key.split('|');
    const emp = employees.find((e) => e.id === empId);
    if (!emp) return;
    const ot = computeOvertime(ents, empId, wk);
    const rate = parseFloat(emp.hourlyRate) || 0;
    const row = rowByEmp[empId] || {
      id: empId, name: emp.name, role: emp.role, rate,
      regHours: 0, otHours: 0, ptoHours: 0,
      regPay: 0, otPay: 0, ptoPay: 0, totalPay: 0,
    };
    row.regHours += ot.regularHours;
    row.otHours += ot.overtimeHours;
    row.ptoHours += ot.ptoHours;
    row.regPay += ot.regularHours * rate;
    row.otPay += ot.overtimeHours * rate * ot.otMultiplier;
    row.ptoPay += ot.ptoHours * rate;
    row.totalPay = row.regPay + row.otPay + row.ptoPay;
    rowByEmp[empId] = row;
  });

  const rows = Object.values(rowByEmp).map((r) => ({
    ...r,
    regHours: parseFloat(r.regHours.toFixed(2)),
    otHours: parseFloat(r.otHours.toFixed(2)),
    ptoHours: parseFloat(r.ptoHours.toFixed(2)),
    regPay: parseFloat(r.regPay.toFixed(2)),
    otPay: parseFloat(r.otPay.toFixed(2)),
    ptoPay: parseFloat(r.ptoPay.toFixed(2)),
    totalPay: parseFloat(r.totalPay.toFixed(2)),
  }));
  rows.sort((a, b) => a.name.localeCompare(b.name));
  const grandTotal = rows.reduce((s, r) => s + r.totalPay, 0);
  return { rows, grandTotal: parseFloat(grandTotal.toFixed(2)) };
};

// Build attendance heatmap data: { employeeId, employeeName, days: [{date, hours}] }.
// `days` is always exactly 14 (rolling 2 weeks ending on `endDate`).
export const buildHeatmapData = (entries, employees, endDate, days = 14) => {
  const end = new Date(endDate); end.setHours(23, 59, 59, 999);
  const start = new Date(end); start.setDate(end.getDate() - days + 1); start.setHours(0, 0, 0, 0);
  const dayList = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    dayList.push(toISODate(d));
  }
  return employees.map((emp) => {
    const dayHours = {};
    entries
      .filter((e) => e.employeeId === emp.id && new Date(e.startTime) >= start && new Date(e.startTime) <= end)
      .forEach((e) => {
        const k = toISODate(new Date(e.startTime));
        dayHours[k] = (dayHours[k] || 0) + liveWorkedMinutes(e) / 60;
      });
    return {
      employeeId: emp.id,
      employeeName: emp.name,
      crewId: emp.crewId,
      days: dayList.map((d) => ({ date: d, hours: parseFloat((dayHours[d] || 0).toFixed(2)) })),
    };
  });
};

// AI scheduling suggestions — heuristic, no LLM call.
// Looks at the last 4 weeks and finds:
//   - Employees who are UNDER-utilized (avg <30h/week) → recommend more shifts
//   - Employees AT-RISK of OT (this week trending toward >40h) → don't add more
//   - Days of the week with thinnest coverage → recommend filling
// Returns an array of human-readable suggestion objects { id, kind, message, score }.
export const generateScheduleSuggestions = (entries, employees, today = new Date()) => {
  const suggestions = [];
  const now = new Date(today);
  const weekStart = startOfWeek(now);
  const fourWeeksAgo = new Date(now); fourWeeksAgo.setDate(now.getDate() - 28);

  // Recent activity per employee
  employees.forEach((emp) => {
    const empEntries = entries.filter(
      (e) => e.employeeId === emp.id && new Date(e.startTime) >= fourWeeksAgo && (!e.type || e.type === 'shift')
    );
    const totalMin = empEntries.reduce((s, e) => s + liveWorkedMinutes(e), 0);
    const avgPerWeek = totalMin / 60 / 4;
    const thisWeek = computeWeeklyStats(entries, emp.id, weekStart);
    const thisWeekHours = thisWeek.regularMin / 60;

    if (avgPerWeek > 0 && avgPerWeek < 20) {
      suggestions.push({
        id: `under_${emp.id}`,
        kind: 'underutilized',
        employeeId: emp.id,
        score: 60 + (20 - avgPerWeek),
        message: `${emp.name} averaged only ${avgPerWeek.toFixed(1)}h/week — consider giving them more shifts.`,
      });
    }
    if (thisWeekHours > 36 && thisWeekHours < 40) {
      suggestions.push({
        id: `near_ot_${emp.id}`,
        kind: 'near-ot',
        employeeId: emp.id,
        score: 80,
        message: `${emp.name} is at ${thisWeekHours.toFixed(1)}h this week — close to OT threshold (40h).`,
      });
    }
    if (thisWeekHours >= 40) {
      suggestions.push({
        id: `ot_${emp.id}`,
        kind: 'ot',
        employeeId: emp.id,
        score: 90,
        message: `${emp.name} is already at ${thisWeekHours.toFixed(1)}h — any additional hours this week trigger OT pay.`,
      });
    }
  });

  // Day-of-week coverage gaps (last 4 weeks). Find the weekday where
  // average daily worked-hours is lowest.
  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dayTotals = [0, 0, 0, 0, 0, 0, 0];
  const dayCounts = [0, 0, 0, 0, 0, 0, 0];
  entries
    .filter((e) => new Date(e.startTime) >= fourWeeksAgo && (!e.type || e.type === 'shift'))
    .forEach((e) => {
      const day = new Date(e.startTime).getDay();
      dayTotals[day] += liveWorkedMinutes(e) / 60;
      dayCounts[day] += 1;
    });
  const dayAvgs = dayTotals.map((t, i) => (dayCounts[i] ? t / dayCounts[i] : 0));
  const nonZero = dayAvgs.filter((v) => v > 0);
  if (nonZero.length >= 3) {
    const min = Math.min(...nonZero);
    const max = Math.max(...nonZero);
    if (max > 0 && min < max * 0.4) {
      const weakestIdx = dayAvgs.findIndex((v) => v === min);
      suggestions.push({
        id: `coverage_${weakestIdx}`,
        kind: 'coverage',
        score: 70,
        message: `${dayLabels[weakestIdx]}s have ${(min).toFixed(1)}h avg coverage — far below your best day. Consider scheduling extra hands.`,
      });
    }
  }

  suggestions.sort((a, b) => b.score - a.score);
  return suggestions.slice(0, 6);
};
